import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, Target, Battery, Layers, Anchor, Moon, MessageCircle, RotateCcw, BookOpen, ArrowRight, Map, ExternalLink, X } from 'lucide-react';
import FutureSelfLetter from './FutureSelfLetter';
import VisualTimelineMap from './VisualTimelineMap';
import TimelineEventDialog from './TimelineEventDialog';
import PivotalStepDialog from './PivotalStepDialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Link, useNavigate } from 'react-router-dom';
interface FutureSelfData {
  future_self_version?: string;
  custom_future_self?: string;
  current_frequency?: string[];
  action_1?: string;
  action_2?: string;
  action_3?: string;
  action_1_completed?: boolean;
  action_2_completed?: boolean;
  action_3_completed?: boolean;
  resonance_category?: string;
  symbolic_anchor?: string;
  evening_reflection?: string;
  future_self_message?: string;
  daily_practicing?: string;
  daily_gratitude?: string;
  daily_identity?: string;
  daily_feeling?: string;
  future_self_guidance?: string;
}
interface FutureSelfSpotlightProps {
  isOpen?: boolean;
  onClose?: () => void;
  initialData?: FutureSelfData;
  standalone?: boolean;
}
const FUTURE_SELF_OPTIONS = ['The Confident Speaker', 'The Calm Leader', 'The Magnetic Creator', 'The Abundant Healer', 'The Loving Partner', 'The Empowered Channel', 'Other'];
const FREQUENCY_OPTIONS = ['Grateful', 'Peaceful', 'Confident', 'Abundant', 'Creative', 'Loving', 'Empowered', 'Intuitive', 'Magnetic', 'Expansive', 'Grounded', 'Radiant'];
const RESONANCE_CATEGORIES = ['Career & Purpose', 'Relationships', 'Health & Vitality', 'Creativity & Expression', 'Finances & Abundance', 'Spiritual Growth', 'Personal Development', 'Family & Home'];
const FutureSelfSpotlight: React.FC<FutureSelfSpotlightProps> = ({
  isOpen = true,
  onClose,
  initialData,
  standalone = false
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [pivotalSteps, setPivotalSteps] = useState<any[]>([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [editingStep, setEditingStep] = useState<any>(null);
  const [futureSelfVersion, setFutureSelfVersion] = useState(initialData?.future_self_version || '');
  const [customFutureSelf, setCustomFutureSelf] = useState(initialData?.custom_future_self || '');
  const [currentFrequency, setCurrentFrequency] = useState<string[]>(initialData?.current_frequency || []);
  const [action1, setAction1] = useState(initialData?.action_1 || '');
  const [action2, setAction2] = useState(initialData?.action_2 || '');
  const [action3, setAction3] = useState(initialData?.action_3 || '');
  const [action1Completed, setAction1Completed] = useState(initialData?.action_1_completed || false);
  const [action2Completed, setAction2Completed] = useState(initialData?.action_2_completed || false);
  const [action3Completed, setAction3Completed] = useState(initialData?.action_3_completed || false);
  const [resonanceCategory, setResonanceCategory] = useState(initialData?.resonance_category || '');
  const [symbolicAnchor, setSymbolicAnchor] = useState(initialData?.symbolic_anchor || '');
  const [eveningReflection, setEveningReflection] = useState(initialData?.evening_reflection || '');
  const [futureSelfMessage, setFutureSelfMessage] = useState(initialData?.future_self_message || '');
  const [dailyPracticing, setDailyPracticing] = useState(initialData?.daily_practicing || '');
  const [dailyGratitude, setDailyGratitude] = useState(initialData?.daily_gratitude || '');
  const [dailyIdentity, setDailyIdentity] = useState(initialData?.daily_identity || '');
  const [dailyFeeling, setDailyFeeling] = useState(initialData?.daily_feeling || '');
  const [futureSelfGuidance, setFutureSelfGuidance] = useState(initialData?.future_self_guidance || '');
  const [showFutureSelfLetter, setShowFutureSelfLetter] = useState(false);
  useEffect(() => {
    if (initialData) {
      setFutureSelfVersion(initialData.future_self_version || '');
      setCustomFutureSelf(initialData.custom_future_self || '');
      setCurrentFrequency(initialData.current_frequency || []);
      setAction1(initialData.action_1 || '');
      setAction2(initialData.action_2 || '');
      setAction3(initialData.action_3 || '');
      setAction1Completed(initialData.action_1_completed || false);
      setAction2Completed(initialData.action_2_completed || false);
      setAction3Completed(initialData.action_3_completed || false);
      setResonanceCategory(initialData.resonance_category || '');
      setSymbolicAnchor(initialData.symbolic_anchor || '');
      setEveningReflection(initialData.evening_reflection || '');
      setFutureSelfMessage(initialData.future_self_message || '');
      setDailyPracticing(initialData.daily_practicing || '');
      setDailyGratitude(initialData.daily_gratitude || '');
      setDailyIdentity(initialData.daily_identity || '');
      setDailyFeeling(initialData.daily_feeling || '');
      setFutureSelfGuidance(initialData.future_self_guidance || '');
    }
  }, [initialData]);

  // Fetch timeline data
  useEffect(() => {
    if (standalone) {
      fetchTimelineData();
    }
  }, [standalone]);
  const fetchTimelineData = async () => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const {
        data: events,
        error: eventsError
      } = await supabase.from('future_self_timeline_events').select('*').eq('user_id', user.id).order('target_date', {
        ascending: true
      });
      if (eventsError) throw eventsError;
      setTimelineEvents(events || []);
      if (events && events.length > 0) {
        const {
          data: steps,
          error: stepsError
        } = await supabase.from('future_self_pivotal_steps').select('*').eq('user_id', user.id).order('sort_order', {
          ascending: true
        });
        if (stepsError) throw stepsError;
        setPivotalSteps(steps || []);
      }
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    }
  };

  // Timeline management handlers
  const handleAddEvent = () => {
    setEditingEvent(null);
    setShowEventDialog(true);
  };
  const handleEditEvent = (event: any) => {
    setEditingEvent(event);
    setShowEventDialog(true);
  };
  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this destination?')) return;
    try {
      const {
        error
      } = await supabase.from('future_self_timeline_events').delete().eq('id', eventId);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Destination deleted successfully"
      });
      fetchTimelineData();
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: "Error",
        description: "Failed to delete destination",
        variant: "destructive"
      });
    }
  };
  const handleSaveEvent = async (eventData: any) => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      if (editingEvent) {
        const {
          error
        } = await supabase.from('future_self_timeline_events').update(eventData).eq('id', editingEvent.id);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Destination updated successfully"
        });
      } else {
        const {
          error
        } = await supabase.from('future_self_timeline_events').insert([{
          ...eventData,
          user_id: user.id
        }]);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Destination created successfully"
        });
      }
      setShowEventDialog(false);
      setEditingEvent(null);
      fetchTimelineData();
    } catch (error) {
      console.error('Error saving event:', error);
      toast({
        title: "Error",
        description: "Failed to save destination",
        variant: "destructive"
      });
    }
  };
  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepDialog(true);
  };
  const handleEditStep = (step: any) => {
    setEditingStep(step);
    setShowStepDialog(true);
  };
  const handleDeleteStep = async (stepId: string) => {
    if (!confirm('Are you sure you want to delete this step?')) return;
    try {
      const {
        error
      } = await supabase.from('future_self_pivotal_steps').delete().eq('id', stepId);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Step deleted successfully"
      });
      fetchTimelineData();
    } catch (error) {
      console.error('Error deleting step:', error);
      toast({
        title: "Error",
        description: "Failed to delete step",
        variant: "destructive"
      });
    }
  };
  const handleSaveStep = async (stepData: any) => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      if (editingStep) {
        const {
          error
        } = await supabase.from('future_self_pivotal_steps').update(stepData).eq('id', editingStep.id);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Step updated successfully"
        });
      } else {
        const {
          error
        } = await supabase.from('future_self_pivotal_steps').insert([{
          ...stepData,
          user_id: user.id
        }]);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Step created successfully"
        });
      }
      setShowStepDialog(false);
      setEditingStep(null);
      fetchTimelineData();
    } catch (error) {
      console.error('Error saving step:', error);
      toast({
        title: "Error",
        description: "Failed to save step",
        variant: "destructive"
      });
    }
  };
  const handleReorderStep = async (stepId: string, direction: 'up' | 'down') => {
    try {
      const stepIndex = pivotalSteps.findIndex(s => s.id === stepId);
      if (stepIndex === -1) return;
      const currentStep = pivotalSteps[stepIndex];
      const swapIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
      if (swapIndex < 0 || swapIndex >= pivotalSteps.length) return;
      const swapStep = pivotalSteps[swapIndex];

      // Swap sort orders
      const {
        error: error1
      } = await supabase.from('future_self_pivotal_steps').update({
        sort_order: swapStep.sort_order
      }).eq('id', currentStep.id);
      const {
        error: error2
      } = await supabase.from('future_self_pivotal_steps').update({
        sort_order: currentStep.sort_order
      }).eq('id', swapStep.id);
      if (error1 || error2) throw error1 || error2;
      toast({
        title: "Success",
        description: "Step reordered successfully"
      });
      fetchTimelineData();
    } catch (error) {
      console.error('Error reordering step:', error);
      toast({
        title: "Error",
        description: "Failed to reorder step",
        variant: "destructive"
      });
    }
  };
  const handleFrequencyToggle = (frequency: string) => {
    setCurrentFrequency(prev => prev.includes(frequency) ? prev.filter(f => f !== frequency) : [...prev, frequency]);
  };
  const handleSave = async () => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const today = new Date().toISOString().split('T')[0];
      const saveData = {
        user_id: user.id,
        date: today,
        future_self_version: futureSelfVersion,
        custom_future_self: customFutureSelf,
        current_frequency: currentFrequency,
        action_1: action1,
        action_2: action2,
        action_3: action3,
        action_1_completed: action1Completed,
        action_2_completed: action2Completed,
        action_3_completed: action3Completed,
        resonance_category: resonanceCategory,
        symbolic_anchor: symbolicAnchor,
        evening_reflection: eveningReflection,
        future_self_message: futureSelfMessage,
        daily_practicing: dailyPracticing,
        daily_gratitude: dailyGratitude,
        daily_identity: dailyIdentity,
        daily_feeling: dailyFeeling,
        future_self_guidance: futureSelfGuidance
      };
      const {
        error
      } = await supabase.from('daily_checkins').upsert(saveData, {
        onConflict: 'user_id,date'
      });
      if (error) throw error;
      toast({
        title: "Future Self Spotlight Saved",
        description: "Your alignment practice has been saved successfully."
      });
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving Future Self data:', error);
      toast({
        title: "Error",
        description: "Failed to save data. Please try again.",
        variant: "destructive"
      });
    }
  };
  if (!isOpen) return null;
  if (showFutureSelfLetter) {
    return <FutureSelfLetter onClose={() => setShowFutureSelfLetter(false)} initialData={{
      future_self_message: futureSelfMessage,
      future_self_guidance: futureSelfGuidance
    }} />;
  }
  const allActionsCompleted = action1Completed && action2Completed && action3Completed;
  const dailySpotlightContent = <div className="space-y-6 max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Align With Your Future Self
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Future Self Version Selection */}
                <div className="space-y-3">
                  <Label htmlFor="future-self" className="text-sm font-medium">Who does your Future Self need you to be today?</Label>
                  <RadioGroup value={futureSelfVersion} onValueChange={setFutureSelfVersion}>
                    <div className="grid grid-cols-2 gap-3">
                      {FUTURE_SELF_OPTIONS.map(option => <div key={option} className="flex items-center space-x-2">
                          <RadioGroupItem value={option} id={option} />
                          <Label htmlFor={option} className="text-sm cursor-pointer">{option}</Label>
                        </div>)}
                    </div>
                  </RadioGroup>
                  
                  {futureSelfVersion === 'Other' && <Input placeholder="Describe your unique Future Self..." value={customFutureSelf} onChange={e => setCustomFutureSelf(e.target.value)} className="mt-2" />}
                </div>

                {/* Current Frequency */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    What frequency are you embodying right now? (Select all that apply)
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {FREQUENCY_OPTIONS.map(frequency => <div key={frequency} className={`p-2 text-xs text-center rounded cursor-pointer transition-all ${currentFrequency.includes(frequency) ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`} onClick={() => handleFrequencyToggle(frequency)}>
                        {frequency}
                      </div>)}
                  </div>
                </div>

                {/* Future Self Actions */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    <Label className="text-sm font-medium">Name 3 things your Future Self would thank you for doing today</Label>
                  </div>
                  
                  {[{
            value: action1,
            setValue: setAction1,
            completed: action1Completed,
            setCompleted: setAction1Completed,
            num: 1
          }, {
            value: action2,
            setValue: setAction2,
            completed: action2Completed,
            setCompleted: setAction2Completed,
            num: 2
          }, {
            value: action3,
            setValue: setAction3,
            completed: action3Completed,
            setCompleted: setAction3Completed,
            num: 3
          }].map(({
            value,
            setValue,
            completed,
            setCompleted,
            num
          }) => <div key={num} className="flex items-center gap-3">
                      <Checkbox id={`action-${num}`} checked={completed} onCheckedChange={checked => setCompleted(checked === true)} className="mt-1" />
                      <Input placeholder={`Future Self Action ${num}...`} value={value} onChange={e => setValue(e.target.value)} className={completed ? 'line-through text-muted-foreground' : ''} />
                    </div>)}
                  
                  {allActionsCompleted && <div className="bg-gradient-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
                      <div className="flex items-center gap-2 text-primary font-medium">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-sm">You're in full Future Self alignment! ✨</span>
                      </div>
                    </div>}
                </div>

                {/* Resonance & Symbolic Anchor */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="resonance" className="text-sm font-medium mb-2 block">
                      Which life area is calling for your attention?
                    </Label>
                    <Select value={resonanceCategory} onValueChange={setResonanceCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {RESONANCE_CATEGORIES.map(category => <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="anchor" className="text-sm font-medium mb-2 block">
                      <div className="flex items-center gap-1">
                        <Anchor className="h-3 w-3" />
                        Symbolic Anchor
                      </div>
                    </Label>
                    <Input id="anchor" placeholder="e.g. golden thread, lighthouse, diamond..." value={symbolicAnchor} onChange={e => setSymbolicAnchor(e.target.value)} />
                  </div>
                </div>

                {/* Message to Future Self */}
                <div className="space-y-3">
                  <Label htmlFor="message" className="text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4" />
                      Message to Your Future Self
                    </div>
                  </Label>
                  <Textarea id="message" placeholder="What do you want to tell your Future Self about today's journey?" className="min-h-[80px]" value={futureSelfMessage} onChange={e => setFutureSelfMessage(e.target.value)} />
                </div>

                <div className="flex justify-center">
                  <Button variant="outline" onClick={() => setShowFutureSelfLetter(true)} className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Write Letter from Future Self
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Evening Questions */}
                <div className="border-t pt-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Moon className="h-4 w-4" />
                    <h4 className="font-medium">Evening Reflections</h4>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="practicing" className="text-sm font-medium mb-2 block">
                        What were you practicing today?
                      </Label>
                      <Input id="practicing" placeholder="e.g. patience, speaking up, trusting..." className="mt-1" value={dailyPracticing} onChange={e => setDailyPracticing(e.target.value)} />
                    </div>

                    <div>
                      <Label htmlFor="gratitude" className="text-sm font-medium mb-2 block">
                        What are you grateful for from today?
                      </Label>
                      <Input id="gratitude" placeholder="I'm grateful for..." className="mt-1" value={dailyGratitude} onChange={e => setDailyGratitude(e.target.value)} />
                    </div>

                    <div>
                      <Label htmlFor="identity" className="text-sm font-medium mb-2 block">What worked well for you today?</Label>
                      <Input id="identity" placeholder="Today I was being..." className="mt-1" value={dailyIdentity} onChange={e => setDailyIdentity(e.target.value)} />
                    </div>

                    <div>
                      <Label htmlFor="feeling" className="text-sm font-medium mb-2 block">
                        How do you want to feel tomorrow?
                      </Label>
                      <Input id="feeling" placeholder="e.g. more confident, deeply connected, truly aligned..." className="mt-1" value={dailyFeeling} onChange={e => setDailyFeeling(e.target.value)} />
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1">
                Save Future Self Spotlight
              </Button>
              {onClose && <Button variant="outline" onClick={onClose}>
                  Close
                </Button>}
            </div>
          </div>;
  const content = standalone ? <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Instructions */}
        <div className="mb-8 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <Map className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-primary-glow to-primary bg-clip-text text-transparent">
                  Future Self Navigation System
                </h1>
                <p className="text-muted-foreground mt-1">Your complete toolkit for timeline transformation</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => goBackOrHome(navigate)}
              className="hover:bg-primary/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <Card className="bg-gradient-to-br from-primary/5 to-background border-primary/20">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Timeline Navigation Coordinates
              </h2>
              <p className="text-muted-foreground">Having a destination without action is like sitting in a car without turning on the engine-you might know where you want to go but you just won't get anywhere. 
This is your destination, route, and the key to turn the engine on. 
This system integrates three essential elements:
            </p>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Target className="h-4 w-4" />
                    <span>Destination</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Define the future moments and pivotal steps on your timeline</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Map className="h-4 w-4" />
                    <span>Route</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Track your daily actions and progress toward each milestone</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Battery className="h-4 w-4" />
                    <span>Engine Key</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Daily alignment practice to embody your future self frequency and power your journey</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2">
              <Map className="h-4 w-4" />
              Timeline Map
            </TabsTrigger>
            <TabsTrigger value="daily" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Daily Practice
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 animate-fade-in">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="hover-scale cursor-pointer" onClick={() => setActiveTab('timeline')}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Map className="h-5 w-5 text-primary" />
                    Timeline Navigation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Design your future timeline with destination points and pivotal steps. Visualise your journey and track progress toward each milestone.
                  </p>
                  <Button className="w-full">
                    Open Timeline Map
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover-scale cursor-pointer" onClick={() => setActiveTab('daily')}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Daily Alignment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Practice embodying your future self frequency. Set daily actions, track your emotional state, and reflect on your progress.
                  </p>
                  <Button className="w-full">
                    Start Daily Practice
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Resources Section */}
            <Card className="bg-gradient-to-br from-background to-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  Deepen Your Practice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground mb-4">
                  Enhance your timeline transformation with guided meditations and comprehensive courses.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="outline" className="flex-1">
                    <Link to="/meditations" className="flex items-center justify-center gap-2">
                      <Moon className="h-4 w-4" />
                      Timeline of Transformation Meditation
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="flex-1">
                    <Link to="/courses" className="flex items-center justify-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Design Your Destiny Course
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeline" className="animate-fade-in">
            <VisualTimelineMap events={timelineEvents} selectedEvent={selectedEvent} pivotalSteps={pivotalSteps} onSelectEvent={setSelectedEvent} onAddEvent={handleAddEvent} onEditEvent={handleEditEvent} onDeleteEvent={handleDeleteEvent} onAddStep={handleAddStep} onEditStep={handleEditStep} onDeleteStep={handleDeleteStep} onReorderStep={handleReorderStep} onRefresh={fetchTimelineData} />
          </TabsContent>

          <TabsContent value="daily" className="animate-fade-in">
            {dailySpotlightContent}
          </TabsContent>
        </Tabs>
      </div>
    </div> : <div className="fixed inset-0 bg-foreground/80 flex items-center justify-center p-4 z-50">
      <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Future Self Spotlight</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>
        <div className="p-6">
          {dailySpotlightContent}
        </div>
      </div>
    </div>;
  if (!isOpen && !standalone) return null;
  if (showFutureSelfLetter) {
    return <FutureSelfLetter onClose={() => setShowFutureSelfLetter(false)} initialData={{
      future_self_message: futureSelfMessage,
      future_self_guidance: futureSelfGuidance
    }} />;
  }
  return <>
      {content}
      
      {/* Dialogs */}
      <TimelineEventDialog open={showEventDialog} onClose={() => {
      setShowEventDialog(false);
      setEditingEvent(null);
    }} onSave={handleSaveEvent} initialData={editingEvent} />

      <PivotalStepDialog open={showStepDialog} onClose={() => {
      setShowStepDialog(false);
      setEditingStep(null);
    }} onSave={handleSaveStep} initialData={editingStep} eventId={selectedEvent?.id} existingSteps={pivotalSteps.filter(s => s.timeline_event_id === selectedEvent?.id)} />
    </>;
};
export default FutureSelfSpotlight;